import { CommerceToolsCart, LineItem } from '@/types/commercetools';
import { CartItem } from '@/hooks/useCartHooks';
import { Locale } from '@/utility/locale';
import { fetchLineItems } from './lineItems/fetchLineItems';
import { trackError } from './raygun';
import { post } from './requestHelpers/clientRequestHelpers';
import { trackCartData, trackCartId } from './tracking/trackCartAction';

export const SPRD_CART_ID = 'sprdCartId';
export const MM_BASKET = 'mmBasket';

export const getCartIdFromStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(SPRD_CART_ID) || null;
};

export const getApiBasketId = () => {
  const mmBasket = window.localStorage.getItem(MM_BASKET);
  if (!mmBasket) return null;
  try {
    const apiBasketId = JSON.parse(mmBasket).apiBasketId;
    return apiBasketId ?? null;
  } catch (error) {
    trackError('Failed to parse MM_BASKET from localStorage', error);
    return null;
  }
};

export const createNewCart = async (
  locale: Locale,
  currency: string | undefined,
  cartItems: CartItem[]
): Promise<CommerceToolsCart> => {
  if (!currency) {
    throw new Error('Currency is undefined');
  }

  // The cart is commercetools, not the legacy platform API: it accepts the
  // canonical locale and rejects the sprd form ("The locale 'dk_DK' is not part
  // of the languages supported in this project"). Do not convert here.
  const cart = await post<CommerceToolsCart>(`/api/v1/carts?locale=${locale}`, {
    currency,
    cartItems,
  });

  window.localStorage.setItem(SPRD_CART_ID, cart.id);
  trackCartId(cart.id);
  return cart;
};

const fetchActiveCart = async (locale: Locale) => {
  const sprdCartId = getCartIdFromStorage();
  if (!sprdCartId) {
    return null;
  }

  return await fetchLineItems(sprdCartId, locale);
};

export const migrateAndFetchCart = async (locale: Locale) => {
  const migrationCartId = getApiBasketId();
  if (migrationCartId) {
    window.localStorage.setItem(SPRD_CART_ID, migrationCartId);
    window.localStorage.removeItem(MM_BASKET);
  }

  const cartAndLineItemGroups = await fetchActiveCart(locale);
  if (cartAndLineItemGroups?.cart.id) {
    trackCartData(cartAndLineItemGroups);
    return cartAndLineItemGroups;
  } else {
    window.localStorage.removeItem(SPRD_CART_ID);
    return null;
  }
};

/**
 * @deprecated the sprdCompositionsJson property is deprecated. Please use parseTrackingJson instead. The function only exists for fallback reasons.
 */
export const parseCompositionsJson = (lineItem: LineItem) => {
  const compositionJson = lineItem.custom.fields.sprdCompositionsJson;
  return compositionJson ? (JSON.parse(compositionJson) as CreateOmatDesign[]) : undefined;
};

/**
 * cart-service serialises the add-to-cart `trackingData` object onto the line item as a JSON
 * string. The object is free-form — it nests properties like `designs` and `templateIds`
 */
export const parseTrackingJson = (lineItem: LineItem): Record<string, unknown> | undefined => {
  const trackingJson = lineItem.custom.fields.sprdTrackingJson;
  if (!trackingJson) return undefined;

  try {
    return JSON.parse(trackingJson) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};
